// src/components/SystemImage.js
import React, { useState, useRef, useEffect } from "react";

// Bug fix: font-size was set as a CSS percentage ("40%"), which resolves
// against the *inherited* font-size, not the icon's own pixel dimensions —
// so the initial rendered at roughly the same tiny size no matter how big
// the icon container was (52px, 80px, whatever). Compute an actual pixel
// value instead, proportional to the container.
function Placeholder({ name, size }) {
  const letter = (name || "?")[0].toUpperCase();
  const px = typeof size === "number" ? Math.max(12, Math.round(size * 0.42)) : 20;
  return (
    <div style={{
      width: "100%", height: "100%", background: "var(--purple-light)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: px, lineHeight: 1, fontWeight: 700, color: "var(--purple)",
      borderRadius: "inherit",
    }}>
      {letter}
    </div>
  );
}

// Display — lazy loads using IntersectionObserver.
// `resource` is the API route segment for this item's type (e.g.
// "saas-applications", "local-applications", "shared-credentials") — each
// type has its own image endpoint, so this must match where the item
// actually lives.
// `cacheKey` should be the system's persisted `imageVersion` (bumped on the
// server every time the image is uploaded/removed) so every screen and
// every user sees the new image immediately, not just the tab that
// uploaded it. eager=true skips the observer and loads immediately (use on
// detail/edit pages where the image is above the fold).
export function SystemImageDisplay({ resource, systemId, systemName, size = 48, radius = 6, cacheKey, eager = false }) {
  const [state, setState] = useState("idle"); // idle | loading | ok | error
  const divRef = useRef();
  const keyRef = useRef(cacheKey ?? systemId);

  // When cacheKey changes (new upload), force a reload of the <img>
  useEffect(() => {
    const next = cacheKey ?? systemId;
    if (next !== keyRef.current) {
      keyRef.current = next;
      setState(prev => (prev === "idle" ? "idle" : "loading"));
    }
  }, [cacheKey, systemId]);

  // Intersection observer for lazy loading. Guarded with a functional
  // update rather than `setState("loading")` directly — this effect reruns
  // every time `state` changes (it's in the dependency array below), so an
  // unguarded call here would fire again right after a successful image
  // load flips state to "ok", forcing it straight back to "loading" and
  // permanently hiding the now-loaded image behind the placeholder.
  useEffect(() => {
    if (eager) { setState(prev => (prev === "idle" ? "loading" : prev)); return; }
    if (state !== "idle") return;
    const el = divRef.current;
    if (!el) return;
    if (!window.IntersectionObserver) { setState("loading"); return; }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setState("loading");
        obs.disconnect();
      }
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [eager, state]);

  const sizeStyle = typeof size === "number"
    ? { width: size, height: size }
    : { width: "100%", aspectRatio: "1" };

  const src = `/api/${resource}/${systemId}/image?v=${keyRef.current}`;

  return (
    <div ref={divRef} style={{ ...sizeStyle, borderRadius: radius, overflow: "hidden", flexShrink: 0, background: "var(--gray-100)", position: "relative" }}>
      {/* Placeholder always rendered underneath — guarantees something is
          always visible even if the image request stalls or never resolves,
          instead of a blank box while state is "loading". */}
      {state !== "ok" && (
        <div style={{ position: "absolute", inset: 0 }}>
          <Placeholder name={systemName} size={size} />
        </div>
      )}
      {(state === "loading" || state === "ok") && (
        <img
          key={keyRef.current}
          src={src}
          alt={systemName}
          onLoad={() => setState("ok")}
          onError={() => setState("error")}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: state === "ok" ? "block" : "none" }}
        />
      )}
    </div>
  );
}

// Upload widget — shown on the system edit page for admins.
// `resource` is the API route segment for this item's type (see above).
// `imageVersion` is the current persisted version (from the loaded system
// record); `onUploaded(newVersion)` should update that record in the parent
// so the preview and every other SystemImageDisplay stay in sync.
export function SystemImageUpload({ resource, systemId, systemName, imageVersion, onUploaded }) {
  const [preview,     setPreview]     = useState(null);
  const [justUploaded, setJustUploaded] = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [error,       setError]       = useState("");
  const inputRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select an image file."); return; }
    if (file.size > 100 * 1024) { setError("Image must be under 100KB — resize or compress before uploading. Most logos are well under this limit."); return; }
    setError("");
    setJustUploaded(false);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const res = await fetch(`/api/${resource}/${systemId}/image`, {
        method:  "POST",
        headers: { "Content-Type": file.type },
        body:    file,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      // Deliberately keep showing the local preview rather than switching
      // back to the live SystemImageDisplay — that requires a fresh page
      // load elsewhere in the app to reliably reflect the new version, so
      // pretending it's "live" here was misleading. Show a clear uploaded
      // confirmation instead and tell them to refresh.
      setJustUploaded(true);
      if (onUploaded) onUploaded(data.imageVersion);
    } catch (err) {
      setError(err.message);
      setPreview(null);
      setJustUploaded(false);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this image?")) return;
    try {
      const res  = await fetch(`/api/${resource}/${systemId}/image`, { method: "DELETE" });
      const data = await res.json();
      setPreview(null);
      setJustUploaded(false);
      if (onUploaded) onUploaded(data.imageVersion);
    } catch (err) {
      setError(err.message);
    }
  };

  const hasImage = !!imageVersion;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
        <div
          style={{ width: 84, height: 84, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: "var(--gray-100)", border: "2px dashed var(--gray-300)", cursor: "pointer", position: "relative" }}
          onClick={() => inputRef.current?.click()}
          title="Click to upload a new image"
        >
          {preview ? (
            <img src={preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <SystemImageDisplay resource={resource} systemId={systemId} systemName={systemName} size={84} radius={8} cacheKey={imageVersion} eager={true} />
          )}
          {justUploaded && (
            <div style={{ position: "absolute", bottom: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "var(--green)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>
              ✓
            </div>
          )}
        </div>
        <div>
          <button
            type="button"
            className="btn-secondary"
            style={{ fontSize: 12, marginBottom: 6, display: "block" }}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Uploading…</> : "📷 Upload Image"}
          </button>
          {hasImage && !uploading && (
            <button type="button" className="btn-ghost" style={{ fontSize: 12, color: "var(--red)" }} onClick={remove}>Remove image</button>
          )}
          <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 4 }}>Square image, max 100KB.<br />PNG or JPG recommended.</div>
        </div>
      </div>
      {justUploaded && (
        <div style={{ fontSize: 12, color: "var(--green)", background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", marginTop: 4 }}>
          ✓ Image uploaded. Refresh the page to see it applied here and across the app.
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 4 }}>{error}</div>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={e => handleFile(e.target.files?.[0])}
        onClick={e => { e.target.value = ""; }}
      />
    </div>
  );
}
